/**
 * WidgetVisualConfig - Configuration visuelle épurée
 * 
 * Composant unifié pour configurer l'apparence de tous les types de widgets.
 * Options simplifiées et pertinentes selon le type de widget.
 */

import React from "react";
import { Typography, Input, Select, Option, Switch, ColorPicker } from "@/components/ui";
import { FaPalette } from "react-icons/fa";
import type {
  VisualConfig,
  ChartVisualConfig,
  KpiVisualConfig,
  TextVisualConfig,
  WordCloudVisualConfig,
  TableVisualConfig,
  DocumentsVisualConfig,
  GraphVisualConfig,
  MapVisualConfig,
  GaugeVisualConfig,
  WidgetType,
} from "@/models/widget-v2";
import { WIDGET_TYPES } from "@/models/widget-v2";
import { chartColorsArray } from "@/styles/colors";

// ============================================================================
// Types
// ============================================================================

interface WidgetVisualConfigProps {
  widgetType: WidgetType;
  config: VisualConfig;
  onChange: (config: VisualConfig) => void;
}

// ============================================================================
// Constants
// ============================================================================

// Couleurs par défaut (utilisées pour l'affichage dans le color picker)
// Les couleurs réelles sont déterminées par useChartColors si colors est vide
const DEFAULT_COLORS = chartColorsArray.slice(0, 5);

const LEGEND_POSITIONS = [
  { value: "top", label: "En haut" },
  { value: "bottom", label: "En bas" },
  { value: "left", label: "À gauche" },
  { value: "right", label: "À droite" },
] as const;

const CURVE_TYPES = [
  { value: "smooth", label: "Lisse" },
  { value: "straight", label: "Droit" },
  { value: "stepline", label: "Escalier" },
] as const;

const MAP_PROJECTIONS = [
  { value: "mercator", label: "Mercator" },
  { value: "equalEarth", label: "Equal Earth" },
  { value: "naturalEarth", label: "Natural Earth" },
] as const;

const DOCUMENTS_VIEWS = [
  { value: "grid", label: "Grille" },
  { value: "list", label: "Liste" },
  { value: "card", label: "Cartes" },
  { value: "timeline", label: "Timeline" },
  { value: "masonry", label: "Maçonnerie" },
  { value: "embed", label: "Embed" },
] as const;

// ============================================================================
// Sub-components
// ============================================================================

interface ColorPaletteEditorProps {
  colors: string[];
  onChange: (colors: string[]) => void;
}

const ColorPaletteEditor: React.FC<ColorPaletteEditorProps> = ({ colors, onChange }) => {
  const displayColors = colors.length > 0 ? colors : DEFAULT_COLORS;
  
  return (
    <div className="space-y-2">
      <Typography variant="text-sm" className="text-content-secondary font-medium">
        Palette de couleurs
      </Typography>
      <div className="flex flex-wrap gap-2">
        {displayColors.map((color, index) => (
          <ColorPicker
            key={index}
            value={color}
            onChange={(newColor) => {
              const newColors = [...displayColors];
              newColors[index] = newColor;
              onChange(newColors);
            }}
            showThemeColors={true}
            showCustomPicker={true}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Chart Visual Editor (Line, Bar, Area, Scatter, Heatmap, Treemap)
// ============================================================================

interface ChartVisualEditorProps {
  config: ChartVisualConfig;
  onChange: (config: ChartVisualConfig) => void;
  widgetType: WidgetType;
}

const ChartVisualEditor: React.FC<ChartVisualEditorProps> = ({
  config,
  onChange,
  widgetType,
}) => {
  const isLineOrArea = widgetType === WIDGET_TYPES.LINE || widgetType === WIDGET_TYPES.AREA;
  const isBar = widgetType === WIDGET_TYPES.BAR;
  // PIE et DONUT partagent le même type (pie_widget)
  const isPieOrDonut = widgetType === WIDGET_TYPES.PIE;
  const isHeatmapOrTreemap = widgetType === WIDGET_TYPES.HEATMAP || widgetType === WIDGET_TYPES.TREEMAP;
  const hasGrid = !isPieOrDonut && !isHeatmapOrTreemap;
  const hasDataLabels = !isHeatmapOrTreemap;
  
  return (
    <div className="space-y-4">
      {/* Titre et sous-titre */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Titre"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder="Titre du widget"
        />
        <Input
          label="Sous-titre"
          value={config.subtitle || ""}
          onChange={(e) => onChange({ ...config, subtitle: e.target.value })}
          placeholder="Sous-titre (optionnel)"
        />
      </div>
      
      {/* Couleurs */}
      <ColorPaletteEditor
        colors={config.colors || []}
        onChange={(colors) => onChange({ ...config, colors })}
      />
      
      {/* Options communes */}
      <div className="grid grid-cols-2 gap-3">
        <Switch
          label="Afficher la légende"
          checked={config.showLegend ?? true}
          onChange={(checked) => onChange({ ...config, showLegend: checked })}
        />
        
        {config.showLegend && (
          <Select
            label="Position de la légende"
            value={config.legendPosition || "top"}
            onChange={(value) => onChange({ ...config, legendPosition: value as ChartVisualConfig["legendPosition"] })}
          >
            {LEGEND_POSITIONS.map(p => (
              <Option key={p.value} value={p.value}>{p.label}</Option>
            ))}
          </Select>
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {hasGrid && (
          <Switch
            label="Afficher la grille"
            checked={config.showGrid ?? true}
            onChange={(checked) => onChange({ ...config, showGrid: checked })}
          />
        )}
        {hasDataLabels && (
          <Switch
            label="Afficher les valeurs"
            checked={config.showDataLabels ?? false}
            onChange={(checked) => onChange({ ...config, showDataLabels: checked })}
          />
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <Switch
          label="Animation"
          checked={config.animation ?? true}
          onChange={(checked) => onChange({ ...config, animation: checked })}
        />
        <Switch
          label="Info-bulles"
          checked={config.tooltips ?? true}
          onChange={(checked) => onChange({ ...config, tooltips: checked })}
        />
      </div>
      
      {/* Options spécifiques ligne/aire */}
      {isLineOrArea && (
        <div className="space-y-3 pt-3 border-t border-arc-border">
          <Typography variant="text-sm" className="text-content-secondary font-medium">
            Options de ligne
          </Typography>
          
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Type de courbe"
              value={config.curveType || "smooth"}
              onChange={(value) => onChange({ ...config, curveType: value as ChartVisualConfig["curveType"] })}
            >
              {CURVE_TYPES.map(c => (
                <Option key={c.value} value={c.value}>{c.label}</Option>
              ))}
            </Select>
            
            <Input
              label="Épaisseur de ligne"
              type="number"
              value={config.strokeWidth || 2}
              onChange={(e) => onChange({ ...config, strokeWidth: parseInt(e.target.value) || 2 })}
              min={1}
              max={10}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <Switch
              label="Afficher les marqueurs"
              checked={config.showMarkers ?? false}
              onChange={(checked) => onChange({ ...config, showMarkers: checked })}
            />
            
            {widgetType === WIDGET_TYPES.AREA && (
              <Input
                label="Opacité du remplissage (%)"
                type="number"
                value={Math.round((config.fillOpacity || 0.3) * 100)}
                onChange={(e) => onChange({ ...config, fillOpacity: (parseInt(e.target.value) || 30) / 100 })}
                min={0}
                max={100}
              />
            )}
          </div>
        </div>
      )}
      
      {/* Options spécifiques barres */}
      {isBar && (
        <div className="space-y-3 pt-3 border-t border-arc-border">
          <Typography variant="text-sm" className="text-content-secondary font-medium">
            Options de barres
          </Typography>
          
          <div className="grid grid-cols-2 gap-3">
            <Switch
              label="Barres horizontales"
              checked={config.horizontal ?? false}
              onChange={(checked) => onChange({ ...config, horizontal: checked })}
            />
            <Switch
              label="Barres empilées"
              checked={config.stacked ?? false}
              onChange={(checked) => onChange({ ...config, stacked: checked })}
            />
          </div>
        </div>
      )}
      
      {/* Options spécifiques pie/donut */}
      {isPieOrDonut && (
        <div className="space-y-3 pt-3 border-t border-arc-border">
          <Typography variant="text-sm" className="text-content-secondary font-medium">
            Options de secteurs
          </Typography>
          
          {/* innerRadius: 0 = pie standard, >0 = donut */}
          <Input
            label="Rayon intérieur (%) - 0 pour pie, >0 pour donut"
            type="number"
            value={config.innerRadius || 0}
            onChange={(e) => onChange({ ...config, innerRadius: parseInt(e.target.value) || 0 })}
            min={0}
            max={90}
          />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// KPI Visual Editor
// ============================================================================

interface KpiVisualEditorProps {
  config: KpiVisualConfig;
  onChange: (config: KpiVisualConfig) => void;
}

const KpiVisualEditor: React.FC<KpiVisualEditorProps> = ({ config, onChange }) => {
  const format = config.format || { type: "number" as const, precision: 0 };
  
  return (
    <div className="space-y-4">
      {/* Titre */}
      <Input
        label="Titre"
        value={config.title || ""}
        onChange={(e) => onChange({ ...config, title: e.target.value })}
        placeholder="Titre du KPI"
      />
      
      {/* Taille de la valeur */}
      <Select
        label="Taille de la valeur"
        value={config.valueSize || "medium"}
        onChange={(value) => onChange({ ...config, valueSize: value as KpiVisualConfig["valueSize"] })}
      >
        <Option value="small">Petite</Option>
        <Option value="medium">Moyenne</Option>
        <Option value="large">Grande</Option>
      </Select>
      
      {/* Options d'affichage */}
      <div className="grid grid-cols-2 gap-3">
        <Switch
          label="Afficher la tendance"
          checked={config.showTrend ?? true}
          onChange={(checked) => onChange({ ...config, showTrend: checked })}
        />
        <Switch
          label="Afficher la comparaison"
          checked={config.showComparison ?? true}
          onChange={(checked) => onChange({ ...config, showComparison: checked })}
        />
      </div>
      
      {/* Format */}
      <div className="space-y-3 pt-3 border-t border-arc-border">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          Format de la valeur
        </Typography>
        
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Type"
            value={format.type}
            onChange={(value) => onChange({
              ...config,
              format: { ...format, type: value as NonNullable<KpiVisualConfig["format"]>["type"] },
            })}
          >
            <Option value="number">Nombre</Option>
            <Option value="currency">Devise</Option>
            <Option value="percentage">Pourcentage</Option>
          </Select>
          
          <Input
            label="Précision décimale"
            type="number"
            value={format.precision || 0}
            onChange={(e) => onChange({
              ...config,
              format: { ...format, precision: parseInt(e.target.value) || 0 },
            })}
            min={0}
            max={4}
          />
        </div>
        
        {format.type === "currency" && (
          <Select
            label="Devise"
            value={format.currency || "EUR"}
            onChange={(value) => onChange({
              ...config,
              format: { ...format, currency: value },
            })}
          >
            <Option value="EUR">Euro (€)</Option>
            <Option value="USD">Dollar ($)</Option>
            <Option value="GBP">Livre (£)</Option>
          </Select>
        )}
        
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Préfixe"
            value={format.prefix || ""}
            onChange={(e) => onChange({
              ...config,
              format: { ...format, prefix: e.target.value },
            })}
            placeholder="ex: +"
          />
          <Input
            label="Suffixe"
            value={format.suffix || ""}
            onChange={(e) => onChange({
              ...config,
              format: { ...format, suffix: e.target.value },
            })}
            placeholder="ex: /mois"
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Gauge Visual Editor
// ============================================================================

interface GaugeVisualEditorProps {
  config: GaugeVisualConfig;
  onChange: (config: GaugeVisualConfig) => void;
}

const GaugeVisualEditor: React.FC<GaugeVisualEditorProps> = ({ config, onChange }) => {
  const format = config.format || { type: "number" as const, precision: 0 };
  
  return (
    <div className="space-y-4">
      {/* Titre */}
      <Input
        label="Titre"
        value={config.title || ""}
        onChange={(e) => onChange({ ...config, title: e.target.value })}
        placeholder="Titre de la jauge"
      />
      
      {/* Min/Max */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Valeur minimum"
          type="number"
          value={config.min ?? 0}
          onChange={(e) => onChange({ ...config, min: parseInt(e.target.value) || 0 })}
        />
        <Input
          label="Valeur maximum"
          type="number"
          value={config.max ?? 100}
          onChange={(e) => onChange({ ...config, max: parseInt(e.target.value) || 100 })}
        />
      </div>
      
      {/* Options d'affichage */}
      <Switch
        label="Afficher la valeur"
        checked={config.showValue ?? true}
        onChange={(checked) => onChange({ ...config, showValue: checked })}
      />
      
      {/* Couleurs */}
      <ColorPaletteEditor
        colors={config.colors || []}
        onChange={(colors) => onChange({ ...config, colors })}
      />
      
      {/* Format */}
      <div className="space-y-3 pt-3 border-t border-arc-border">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          Format de la valeur
        </Typography>
        
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Type"
            value={format.type}
            onChange={(value) => onChange({
              ...config,
              format: { ...format, type: value as NonNullable<GaugeVisualConfig["format"]>["type"] },
            })}
          >
            <Option value="number">Nombre</Option>
            <Option value="currency">Devise</Option>
            <Option value="percentage">Pourcentage</Option>
          </Select>
          
          <Input
            label="Précision décimale"
            type="number"
            value={format.precision || 0}
            onChange={(e) => onChange({
              ...config,
              format: { ...format, precision: parseInt(e.target.value) || 0 },
            })}
            min={0}
            max={4}
          />
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Préfixe"
            value={format.prefix || ""}
            onChange={(e) => onChange({
              ...config,
              format: { ...format, prefix: e.target.value },
            })}
            placeholder="ex: +"
          />
          <Input
            label="Suffixe"
            value={format.suffix || ""}
            onChange={(e) => onChange({
              ...config,
              format: { ...format, suffix: e.target.value },
            })}
            placeholder="ex: %"
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Text Visual Editor
// ============================================================================

interface TextVisualEditorProps {
  config: TextVisualConfig;
  onChange: (config: TextVisualConfig) => void;
}

const TextVisualEditor: React.FC<TextVisualEditorProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Titre */}
      <Input
        label="Titre"
        value={config.title || ""}
        onChange={(e) => onChange({ ...config, title: e.target.value })}
        placeholder="Titre du bloc de texte"
      />
      
      {/* Options d'apparence */}
      <div className="space-y-3">
        <Switch
          label="Fond transparent"
          checked={config.transparentBackground ?? true}
          onChange={(checked) => onChange({ ...config, transparentBackground: checked })}
        />
        
        {!config.transparentBackground && (
          <div className="flex items-center gap-3">
            <Typography variant="text-sm" className="text-content-secondary">
              Couleur de fond
            </Typography>
            <ColorPicker
              value={config.backgroundColor || "var(--arc-surface-elevated)"}
              onChange={(color) => onChange({ ...config, backgroundColor: color })}
              showThemeColors={true}
              showCustomPicker={true}
            />
          </div>
        )}
        
        <Input
          label="Espacement interne (px)"
          type="number"
          value={config.padding || 8}
          onChange={(e) => onChange({ ...config, padding: parseInt(e.target.value) || 8 })}
          min={0}
          max={64}
        />
      </div>
    </div>
  );
};

// ============================================================================
// WordCloud Visual Editor
// ============================================================================

interface WordCloudVisualEditorProps {
  config: WordCloudVisualConfig;
  onChange: (config: WordCloudVisualConfig) => void;
}

const WordCloudVisualEditor: React.FC<WordCloudVisualEditorProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Titre et sous-titre */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Titre"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder="Titre du nuage de mots"
        />
        <Input
          label="Sous-titre"
          value={config.subtitle || ""}
          onChange={(e) => onChange({ ...config, subtitle: e.target.value })}
          placeholder="Sous-titre (optionnel)"
        />
      </div>
      
      {/* Couleurs */}
      <ColorPaletteEditor
        colors={config.colors || []}
        onChange={(colors) => onChange({ ...config, colors })}
      />
      
      {/* Options de taille */}
      <div className="space-y-3 pt-3 border-t border-arc-border">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          Options d'affichage
        </Typography>
        
        <Input
          label="Nombre maximum de mots"
          type="number"
          value={config.maxWords || 80}
          onChange={(e) => onChange({ ...config, maxWords: parseInt(e.target.value) || 80 })}
          min={10}
          max={200}
        />
        
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Taille min. (px)"
            type="number"
            value={config.minFontSize || 12}
            onChange={(e) => onChange({ ...config, minFontSize: parseInt(e.target.value) || 12 })}
            min={8}
            max={32}
          />
          <Input
            label="Taille max. (px)"
            type="number"
            value={config.maxFontSize || 64}
            onChange={(e) => onChange({ ...config, maxFontSize: parseInt(e.target.value) || 64 })}
            min={24}
            max={128}
          />
        </div>
        
        <Switch
          label="Rotation des mots"
          checked={config.rotationEnabled ?? true}
          onChange={(checked) => onChange({ ...config, rotationEnabled: checked })}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Table Visual Editor
// ============================================================================

interface TableVisualEditorProps {
  config: TableVisualConfig;
  onChange: (config: TableVisualConfig) => void;
}

const TableVisualEditor: React.FC<TableVisualEditorProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Titre et sous-titre */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Titre"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder="Titre du tableau"
        />
        <Input
          label="Sous-titre"
          value={config.subtitle || ""}
          onChange={(e) => onChange({ ...config, subtitle: e.target.value })}
          placeholder="Sous-titre (optionnel)"
        />
      </div>
      
      {/* Options d'affichage */}
      <div className="space-y-3 pt-3 border-t border-arc-border">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          Options d'affichage
        </Typography>
        
        <div className="grid grid-cols-2 gap-3">
          <Switch
            label="Lignes alternées"
            checked={config.striped ?? true}
            onChange={(checked) => onChange({ ...config, striped: checked })}
          />
          <Switch
            label="Mode compact"
            checked={config.compact ?? false}
            onChange={(checked) => onChange({ ...config, compact: checked })}
          />
        </div>
        
        <Switch
          label="Numéros de ligne"
          checked={config.showRowNumbers ?? false}
          onChange={(checked) => onChange({ ...config, showRowNumbers: checked })}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Documents Visual Editor
// ============================================================================

interface DocumentsVisualEditorProps {
  config: DocumentsVisualConfig;
  onChange: (config: DocumentsVisualConfig) => void;
}

const DocumentsVisualEditor: React.FC<DocumentsVisualEditorProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Titre */}
      <Input
        label="Titre"
        value={config.title || ""}
        onChange={(e) => onChange({ ...config, title: e.target.value })}
        placeholder="Titre du widget"
      />
      
      {/* Vue par défaut */}
      <Select
        label="Vue par défaut"
        value={config.defaultView || "card"}
        onChange={(value) => onChange({ ...config, defaultView: value as DocumentsVisualConfig["defaultView"] })}
      >
        {DOCUMENTS_VIEWS.map(v => (
          <Option key={v.value} value={v.value}>{v.label}</Option>
        ))}
      </Select>
    </div>
  );
};

// ============================================================================
// Graph Visual Editor
// ============================================================================

interface GraphVisualEditorProps {
  config: GraphVisualConfig;
  onChange: (config: GraphVisualConfig) => void;
}

const GraphVisualEditor: React.FC<GraphVisualEditorProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Titre */}
      <Input
        label="Titre"
        value={config.title || ""}
        onChange={(e) => onChange({ ...config, title: e.target.value })}
        placeholder="Titre du graphe"
      />
      
      {/* Options d'affichage */}
      <div className="space-y-3 pt-3 border-t border-arc-border">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          Options d'affichage
        </Typography>
        
        <div className="grid grid-cols-2 gap-3">
          <Switch
            label="Afficher la légende"
            checked={config.showLegend ?? true}
            onChange={(checked) => onChange({ ...config, showLegend: checked })}
          />
          <Switch
            label="Afficher les labels"
            checked={config.showLabels ?? true}
            onChange={(checked) => onChange({ ...config, showLabels: checked })}
          />
        </div>
        
        <Switch
          label="Info-bulles"
          checked={config.showTooltips ?? true}
          onChange={(checked) => onChange({ ...config, showTooltips: checked })}
        />
      </div>
      
      {/* Options de simulation */}
      <div className="space-y-3 pt-3 border-t border-arc-border">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          Paramètres du graphe
        </Typography>
        
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Taille des nœuds"
            type="number"
            value={config.nodeSize || 15}
            onChange={(e) => onChange({ ...config, nodeSize: parseInt(e.target.value) || 15 })}
            min={5}
            max={50}
          />
          <Input
            label="Distance des liens"
            type="number"
            value={config.linkDistance || 30}
            onChange={(e) => onChange({ ...config, linkDistance: parseInt(e.target.value) || 30 })}
            min={10}
            max={200}
          />
        </div>
        
        <Input
          label="Force de répulsion"
          type="number"
          value={Math.abs(config.chargeStrength || 200)}
          onChange={(e) => onChange({ ...config, chargeStrength: -(parseInt(e.target.value) || 200) })}
          min={50}
          max={500}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Map Visual Editor
// ============================================================================

interface MapVisualEditorProps {
  config: MapVisualConfig;
  onChange: (config: MapVisualConfig) => void;
}

const MapVisualEditor: React.FC<MapVisualEditorProps> = ({ config, onChange }) => {
  return (
    <div className="space-y-4">
      {/* Titre */}
      <Input
        label="Titre"
        value={config.title || ""}
        onChange={(e) => onChange({ ...config, title: e.target.value })}
        placeholder="Titre de la carte"
      />
      
      {/* Couleurs */}
      <ColorPaletteEditor
        colors={config.colors || []}
        onChange={(colors) => onChange({ ...config, colors })}
      />
      
      {/* Options de la carte */}
      <div className="space-y-3 pt-3 border-t border-arc-border">
        <Typography variant="text-sm" className="text-content-secondary font-medium">
          Options de la carte
        </Typography>
        
        <Select
          label="Projection"
          value={config.projection || "mercator"}
          onChange={(value) => onChange({ ...config, projection: value as MapVisualConfig["projection"] })}
        >
          {MAP_PROJECTIONS.map(p => (
            <Option key={p.value} value={p.value}>{p.label}</Option>
          ))}
        </Select>
        
        <Input
          label="Zoom par défaut"
          type="number"
          value={config.defaultZoom || 1}
          onChange={(e) => onChange({ ...config, defaultZoom: parseFloat(e.target.value) || 1 })}
          min={0.5}
          max={10}
          step={0.5}
        />
        
        <div className="grid grid-cols-2 gap-3">
          <Switch
            label="Afficher la légende"
            checked={config.showLegend ?? true}
            onChange={(checked) => onChange({ ...config, showLegend: checked })}
          />
          <Switch
            label="Labels des marqueurs"
            checked={config.showMarkerLabels ?? true}
            onChange={(checked) => onChange({ ...config, showMarkerLabels: checked })}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const WidgetVisualConfig: React.FC<WidgetVisualConfigProps> = ({
  widgetType,
  config,
  onChange,
}) => {
  const renderEditor = () => {
    switch (widgetType) {
      case WIDGET_TYPES.KPI:
        return (
          <KpiVisualEditor
            config={config as KpiVisualConfig}
            onChange={onChange}
          />
        );
        
      case WIDGET_TYPES.GAUGE:
        return (
          <GaugeVisualEditor
            config={config as GaugeVisualConfig}
            onChange={onChange}
          />
        );
        
      case WIDGET_TYPES.TEXT:
        return (
          <TextVisualEditor
            config={config as TextVisualConfig}
            onChange={onChange}
          />
        );
        
      case WIDGET_TYPES.WORDCLOUD:
        return (
          <WordCloudVisualEditor
            config={config as WordCloudVisualConfig}
            onChange={onChange}
          />
        );
        
      case WIDGET_TYPES.TABLE:
        return (
          <TableVisualEditor
            config={config as TableVisualConfig}
            onChange={onChange}
          />
        );
        
      case WIDGET_TYPES.DOCUMENTS:
        return (
          <DocumentsVisualEditor
            config={config as DocumentsVisualConfig}
            onChange={onChange}
          />
        );
        
      case WIDGET_TYPES.GRAPH:
        return (
          <GraphVisualEditor
            config={config as GraphVisualConfig}
            onChange={onChange}
          />
        );
        
      case WIDGET_TYPES.MAP:
        return (
          <MapVisualEditor
            config={config as MapVisualConfig}
            onChange={onChange}
          />
        );
        
      // Charts (line, bar, area, pie, donut, scatter, heatmap, treemap)
      default:
        return (
          <ChartVisualEditor
            config={config as ChartVisualConfig}
            onChange={onChange}
            widgetType={widgetType}
          />
        );
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-content-secondary">
        <FaPalette size={16} />
        <Typography variant="text-sm" className="font-semibold">
          Configuration visuelle
        </Typography>
      </div>
      
      {renderEditor()}
    </div>
  );
};

export default WidgetVisualConfig;
